import { SegmentKind } from "@/services/backend/rlt/treatments/types/segmentKind";
import { Filter } from "@/shared/lib/filter/filter";
import { Address } from "@/services/backend/addresses/types/address";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import {
  downloadResponseHandler,
  previewResponseHandler,
} from "@/services/backend/_lib/pdf-helpers";
import { Lst } from "@/shared/lib/lst/lst";
import { providesList } from "@/services/backend/_lib/tag-helpers";
import { backendApi } from "@/services/backend/api";
import { Certificate } from "@/services/backend/rlt/certificates/types/certificate";

const rltCertificatesApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    rltCertificateShow: build.query<Certificate, ShowRequest>({
      query: (request) => ({
        url: "/rlt/certificate",
        method: "GET",
        params: request,
      }),
      providesTags: (_result, _error, request) => [
        { type: "RltCertificate", id: request.id },
      ],
    }),
    rltCertificateList: build.query<Lst<Certificate>, ListRequest>({
      query: (request) => ({
        url: "/rlt/certificate/list",
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) => providesList(result?.items, "RltCertificate"),
    }),
    rltCertificateCreate: build.mutation<Certificate, CreateRequest>({
      query: (request) => ({
        url: "/rlt/certificate",
        method: "POST",
        body: request,
      }),
      invalidatesTags: [{ type: "RltCertificate", id: "LIST" }],
    }),
    rltCertificateUpdateIssueDate: build.mutation<
      Certificate,
      UpdateIssueDateRequest
    >({
      query: (request) => ({
        url: `/rlt/certificate/issue-date`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltCertificate", id: request.id },
        { type: "RltCertificate", id: "LIST" },
      ],
    }),
    rltCertificateUpdateWorkStartedAt: build.mutation<
      Certificate,
      UpdateWorkStartedAtRequest
    >({
      query: (request) => ({
        url: "/rlt/certificate/work-started-at",
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltCertificate", id: request.id },
        { type: "RltCertificate", id: "LIST" },
      ],
    }),
    rltCertificateUpdateWorkEndedAt: build.mutation<
      Certificate,
      UpdateWorkEndedAtRequest
    >({
      query: (request) => ({
        url: "/rlt/certificate/work-ended-at",
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltCertificate", id: request.id },
        { type: "RltCertificate", id: "LIST" },
      ],
    }),
    rltCertificateUpdatePostalAddress: build.mutation<
      Certificate,
      UpdatePostalAddressRequest
    >({
      query: (request) => ({
        url: `/rlt/certificate/postal-address`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltCertificate", id: request.id },
        "RltCertificate",
      ],
    }),
    rltCertificateUpdateServiceAddress: build.mutation<
      Certificate,
      UpdateServiceAddressRequest
    >({
      query: (request) => ({
        url: `/rlt/certificate/service-address`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltCertificate", id: request.id },
        "RltCertificate",
      ],
    }),
    rltCertificateUpdateSegments: build.mutation<
      Certificate,
      UpdateSegmentsRequest
    >({
      query: (request) => ({
        url: "/rlt/certificate/segments",
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltCertificate", id: request.id },
        { type: "RltCertificate", id: "LIST" },
      ],
    }),
    rltCertificatePdfPreview: build.query<{ objectURL: string }, PDFRequest>({
      query: (request) => ({
        url: "/rlt/certificate",
        method: "GET",
        params: request,
        headers: { Accept: "application/pdf" },
        responseHandler: previewResponseHandler,
        // IMPORTANT. Since this query will download the PDF under no circumstances
        // should the results be cached. This could end up using the users memory.
        cache: "no-cache",
      }),
      providesTags: (_result, _error, request) => [
        { type: "RltCertificate", id: request.id },
      ],
    }),
    rltCertificatePdfDownload: build.mutation<
      { filename: string; objectURL: string },
      PDFRequest
    >({
      query: (request) => ({
        url: "/rlt/certificate",
        method: "GET",
        params: { ...request, download: true },
        headers: { Accept: "application/pdf" },
        responseHandler: downloadResponseHandler,
        // IMPORTANT. Since this mutation will download the PDF under no circumstances
        // should the results be cached. This could end up using the users memory.
        cache: "no-cache",
      }),
    }),
    rltCertificateSetImmutable: build.mutation<
      Certificate,
      SetImmutableRequest
    >({
      query: (request) => ({
        url: `/rlt/certificate/set-immutable`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "RltCertificate", id: request.id },
        "RltCertificate",
        "NumbersDocumentNumber",
      ],
    }),
    rltCertificateDelete: build.mutation<null, DeleteRequest>({
      query: (request) => ({
        url: "/rlt/certificate",
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltCertificate", id: request.id },
        { type: "RltCertificate", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useRltCertificateCreateMutation,
  useRltCertificateShowQuery,
  useRltCertificateListQuery,
  useRltCertificateUpdateIssueDateMutation,
  useRltCertificateUpdateWorkStartedAtMutation,
  useRltCertificateUpdateWorkEndedAtMutation,
  useRltCertificateUpdatePostalAddressMutation,
  useRltCertificateUpdateServiceAddressMutation,
  useRltCertificateUpdateSegmentsMutation,
  useRltCertificateSetImmutableMutation,
  useRltCertificatePdfPreviewQuery,
  useRltCertificatePdfDownloadMutation,
  useRltCertificateDeleteMutation,
} = rltCertificatesApi;

export interface ShowRequest {
  id: string;
}

export interface CreateRequest {
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface UpdateIssueDateRequest {
  id: string;
  issueDate: string;
}

export interface UpdateWorkStartedAtRequest {
  id: string;
  workStartedAt: string;
}

export interface UpdateWorkEndedAtRequest {
  id: string;
  workEndedAt: string;
}

export interface UpdatePostalAddressRequest {
  id: string;
  postalAddress: Address;
}

export interface UpdateServiceAddressRequest {
  id: string;
  serviceAddress: Address;
}

export interface UpdateSegmentsRequest {
  id: string;
  segments: SegmentKind[];
}

export interface PDFRequest {
  id: string;
}

export interface SetImmutableRequest {
  id: string;
  setBy: string;
}

export interface ListRequest
  extends DocumentBaseFilters, DocumentCommonFilters {
  orderBy?: OrdrBy<string>[];
  issueDate?: Filter<string>;
  immutable?: Filter<boolean>;
  limit?: number;
  offset?: number;
}

export interface DeleteRequest {
  id: string;
}
